import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/services/api";
import { SITE_LOGO } from "@/constants/assets";
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Sliders,
  UserCircle,
  Globe
} from "lucide-react";

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.getDashboardStats(token);
      if (!response.error) {
        setStats(response);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-white">Dashboard</h1>
          <p className="text-gray-400 mt-2">Welcome back, {user?.name}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Submissions"
                value={stats?.total || 0}
                icon={FileText}
                color="#c4646a"
              />
              <StatCard
                title="New Leads"
                value={stats?.by_status?.new || 0}
                icon={AlertCircle}
                color="#60a5fa"
              />
              <StatCard
                title="Contacted"
                value={stats?.by_status?.contacted || 0}
                icon={Clock}
                color="#fbbf24"
              />
              <StatCard
                title="Qualified"
                value={stats?.by_status?.qualified || 0}
                icon={CheckCircle}
                color="#4ade80"
              />
            </div>

            {/* Recent Submissions */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-medium text-white">Recent Submissions</h2>
                <Link
                  to="/admin/submissions"
                  className="text-[#c4646a] hover:text-[#d4848a] text-sm"
                >
                  View All →
                </Link>
              </div>

              {stats?.recent?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent.map((submission) => (
                    <Link
                      key={submission.reference_id}
                      to={`/admin/submissions/${submission.id}`}
                      className="block bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{submission.couple_names}</p>
                          <p className="text-gray-400 text-sm">{submission.reference_id}</p>
                        </div>
                        <span className={`status-badge status-${submission.status}`}>
                          {submission.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No submissions yet</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <p className="text-3xl font-medium text-white">{value}</p>
        </div>
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Shared Admin Layout
export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/submissions", icon: FileText, label: "Submissions" },
    { path: "/admin/form-options", icon: Sliders, label: "Form Options", roles: ["super_admin"] },
    { path: "/admin/landing", icon: Globe, label: "Landing Page", roles: ["super_admin"] },
    { path: "/admin/users", icon: Users, label: "Users", roles: ["super_admin"] },
    { path: "/admin/settings", icon: Settings, label: "Settings", roles: ["super_admin"] }
  ];

  const filteredNavItems = navItems.filter(
    item => !item.roles || item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 w-64 z-50 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <img
            src={SITE_LOGO}
            alt="Inventive Pages"
            className="h-16 w-auto"
          />
        </div>

        {/* Navigation */}
        <nav className="py-6">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item ${
                location.pathname === item.path ? "active" : ""
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <Link
            to="/admin/profile"
            className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="w-10 h-10 rounded-full bg-[#c4646a]/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[#c4646a] font-medium">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-gray-500 text-xs capitalize">{user?.role?.replace(/_/g, " ")}</p>
            </div>
            <UserCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 w-full">
        {/* Mobile Header */}
        <header className="lg:hidden bg-[#0a0a0a] border-b border-white/10 p-4 flex items-center gap-3 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white p-1 -ml-1"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-white text-sm font-medium truncate">Admin Portal</span>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto min-w-0 bg-[#0a0a0a]">
          {children}
        </main>
      </div>
    </div>
  );
}
